import { supabase, isConfigured } from './supabase';
import storage from './storage';
import { mergeLeadArrays, mergeListMeta, mergeRecordsById, TEAM_KEYS } from '../utils/teamCrmStore';
import { getSyncMeta, setSyncMetaEntry, SYNC_CONFLICT_EVENT } from './userDataSync';

const TABLE = 'crm_team_snapshot';

const DATASETS = ['leads', 'lists', 'tasks', 'meetings', 'views'];

function dispatchSyncError(message, detail) {
  try {
    window.dispatchEvent(new CustomEvent('crm-error', {
      detail: { code: 'TEAM_CRM_CLOUD', message, detail: String(detail || '') },
    }));
  } catch { /* ignore */ }
}

function parseTs(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function dispatchTeamConflict(key, message, remoteValue, remoteUpdatedAt) {
  try {
    window.dispatchEvent(new CustomEvent(SYNC_CONFLICT_EVENT, {
      detail: { key, message, remoteValue, remoteUpdatedAt },
    }));
  } catch { /* ignore */ }
}

function localKeyForDataset(dataset) {
  const map = {
    leads: TEAM_KEYS.leads,
    lists: TEAM_KEYS.lists,
    tasks: TEAM_KEYS.tasks,
    meetings: TEAM_KEYS.meetings,
    views: TEAM_KEYS.views,
  };
  return map[dataset];
}

function mergeDataset(dataset, local, remote) {
  if (dataset === 'leads') return mergeLeadArrays(local, remote);
  if (dataset === 'lists') return mergeListMeta(local, remote);
  if (dataset === 'views') return mergeRecordsById(local, remote);
  return mergeRecordsById(local, remote);
}

/** Cloud-Stand für einen Datensatz lokal übernehmen (nach Konflikt-Banner). */
export function applyRemoteTeamDataset(storageKey, remoteValue, remoteUpdatedAt) {
  if (!storageKey) return;
  const value = Array.isArray(remoteValue) ? remoteValue : [];
  storage.setLocal(storageKey, value);
  setSyncMetaEntry(storageKey, {
    updatedAt: remoteUpdatedAt || new Date().toISOString(),
    source: 'remote',
  });
  try {
    window.dispatchEvent(new CustomEvent('crm-team-data-changed', { detail: { source: 'cloud-override' } }));
  } catch { /* ignore */ }
}

/**
 * Lädt Team-Snapshots aus Supabase und merged sie lokal (Leads/Listen/Tasks/Meetings).
 */
export async function pullTeamCrmIntoLocal() {
  if (!isConfigured) return;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { data, error } = await supabase.from(TABLE).select('dataset, value, updated_at');
  if (error) {
    dispatchSyncError('Team-Daten konnten nicht geladen werden.', error.message);
    throw error;
  }

  for (const row of data || []) {
    const ds = row.dataset;
    if (!DATASETS.includes(ds)) continue;
    const key = localKeyForDataset(ds);
    const remote = Array.isArray(row.value) ? row.value : [];
    const local = storage.get(key, []);
    const remoteTs = parseTs(row.updated_at);
    const localTs = parseTs(getSyncMeta()[key]?.updatedAt);
    const sameContent = JSON.stringify(local) === JSON.stringify(remote);

    if (sameContent) {
      if (row.updated_at) setSyncMetaEntry(key, { updatedAt: row.updated_at, source: 'remote' });
      continue;
    }

    if (remoteTs > localTs) {
      const merged = mergeDataset(ds, local, remote);
      storage.setLocal(key, merged);
      setSyncMetaEntry(key, { updatedAt: row.updated_at, source: 'remote' });
      continue;
    }

    if (localTs > remoteTs) {
      continue;
    }

    const merged = mergeDataset(ds, local, remote);
    storage.setLocal(key, merged);
    if (row.updated_at) setSyncMetaEntry(key, { updatedAt: row.updated_at, source: 'merged' });
  }

  try {
    window.dispatchEvent(new CustomEvent('crm-team-data-changed', { detail: { source: 'cloud' } }));
  } catch { /* ignore */ }
}

export async function pushDatasetToTeamCloud(dataset, value) {
  if (!isConfigured || !DATASETS.includes(dataset)) return;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  // Mark as self-push so realtime echo is ignored — prevents push→reload→push loop
  _recentSelfPushes.add(dataset);
  setTimeout(() => _recentSelfPushes.delete(dataset), 15000);

  const payload = {
    dataset,
    value: Array.isArray(value) ? value : [],
    updated_at: new Date().toISOString(),
    updated_by: user.id,
  };

  const { error } = await supabase.from(TABLE).upsert(payload, { onConflict: 'dataset' });
  if (error) dispatchSyncError(`Team-${dataset} konnte nicht synchronisiert werden.`, error.message);
}

// Datasets recently pushed by THIS client — skip realtime echoes for these
const _recentSelfPushes = new Set();

let _activeSnapshotChannel = null;

/** Realtime: andere Nutzer haben Leads/Listen aktualisiert. */
export function subscribeTeamCrmSnapshot(onChange) {
  if (!isConfigured) return () => {};

  if (!_activeSnapshotChannel) {
    _activeSnapshotChannel = supabase
      .channel('crm_team_snapshot_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: TABLE }, (payload) => {
        // Skip self-push echoes — prevents the push → realtime → reload → push loop
        const ds = payload?.new?.dataset;
        if (ds && _recentSelfPushes.has(ds)) return;
        pullTeamCrmIntoLocal()
          .then(() => onChange?.())
          .catch(() => {});
      })
      .subscribe();
  }

  return () => {
    try { supabase.removeChannel(_activeSnapshotChannel); } catch { /* ignore */ }
    _activeSnapshotChannel = null;
  };
}
